import React, { lazy } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Preloader from 'components/Preloader';
import Helmet from 'components/Helmet';
import MainLayout from 'layouts/MainLayout';
import getCurrentId from 'helpers/getCurrentId';
import { getInstitutionInfo } from 'actions/university';
import { triggerDailyQuest } from 'actions/gamification';
import { DAILY } from 'variables/main';
import { getLanguage } from 'translation';
import { getLocalizedPath } from 'helpers/getLocale';
const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const PremiumLayout = React.lazy(() => import('./components/PremiumLayout'));
const DefaultLayout = React.lazy(() => import('./components/DefaultLayout'));

const University = () => {
  const t = useTranslate('HELMET');
  const universityTranslate = useTranslate('University');
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isPremium] = React.useState(false);
  const [currentId] = React.useState(getCurrentId(location));
  const [institutionInfo, setInstitutionInfo] = React.useState(null);
  const [lang] = React.useState(getLanguage().toUpperCase());
  const contentRef = React.useRef(null);

  React.useEffect(() => {
    const fetchData = async () => {
      const result = await dispatch(getInstitutionInfo(currentId));
      const data = result[0];

      if (!data) {
        navigate(getLocalizedPath('/education'));
        return;
      }

      setInstitutionInfo(data);
    };
    if (currentId) fetchData();
  }, [currentId, navigate, dispatch]);

  React.useEffect(() => {
    const listenScroll = () => {
      if (!contentRef?.current) return;
      const isBottom = contentRef.current.getBoundingClientRect().bottom <= window.innerHeight;
      if (!isBottom) return;
      dispatch(triggerDailyQuest(DAILY.readEducationalProgram));
      window.removeEventListener('scroll', listenScroll);
    };

    window.addEventListener('scroll', listenScroll);

    return () => window.removeEventListener('scroll', listenScroll);
  }, [dispatch, contentRef]);

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: 'EducationProgramsPage',
        link: '/education'
      },
      {
        label: 'Universities'
      }
    ];
  }, []);

  if (!institutionInfo) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        meta={{
          title: t('universityTitle', {
            name:
              institutionInfo[`name${lang}`] +
              ' ' +
              universityTranslate(`${institutionInfo?.institutionType}Type`)
          }),
          description: institutionInfo?.description || t('universityDescription'),
          ogTitle: institutionInfo?.[`name${lang}`]
            ? t('universityOgTitle', {
                name:
                  institutionInfo[`name${lang}`] +
                  ' ' +
                  universityTranslate(`${institutionInfo?.institutionType}Type`)
              })
            : t('universityOgTitle', { name: '' }),
          ogDescription: institutionInfo?.description || t('universityOgDescription')
        }}
      />
      <div ref={contentRef}>
        <MainLayout>
          <Header />
          {isPremium ? (
            <PremiumLayout breadcrumbs={breadcrumbs} institutionInfo={institutionInfo} />
          ) : (
            <DefaultLayout breadcrumbs={breadcrumbs} institutionInfo={institutionInfo} />
          )}
          <Footer />
        </MainLayout>
      </div>
    </>
  );
};

export default University;
